import React, { useState } from 'react'
import { useDashboardStore } from '../../store/dashboardStore'
import { ChartContainer } from '../charts/ChartContainer'

export function ValidationHub() {
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null)
  const strategies = useDashboardStore((state) => state.strategies)
  
  // Filter survivors for validation analysis
  const survivors = strategies.filter(s => s.is_survivor)
  
  return (
    <div className="validation-hub">
      <div className="page-header">
        <div>
          <h1 className="page-title">Validation Hub</h1>
          <p className="page-subtitle">
            Out-of-sample performance verification and holdout testing
          </p>
        </div>
        
        <div className="page-actions">
          <button className="btn btn--secondary">
            <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export Results
          </button>
          
          <button className="btn btn--primary">
            <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Run Validation
          </button>
        </div>
      </div>

      {/* Validation Overview */}
      <div className="grid grid--validation-hero mb-6">
        <div className="card card--validation-split">
          <div className="card__header">
            <h2 className="card__title">In-Sample vs Out-of-Sample</h2>
            <p className="card__subtitle">
              Performance comparison across training and holdout periods
            </p>
          </div>
          <div className="card__content">
            <div className="validation-split">
              <div className="validation-panel validation-panel--in-sample">
                <div className="validation-panel__header">
                  <h3 className="validation-panel__title">In-Sample (Training)</h3>
                  <div className="validation-panel__period">Jan 2023 - Oct 2023</div>
                </div>
                <div className="validation-panel__metrics">
                  <div className="metric">
                    <div className="metric__label">Average DSR</div>
                    <div className="metric__value metric__value--positive">0.847</div>
                  </div>
                  <div className="metric">
                    <div className="metric__label">Average Return</div>
                    <div className="metric__value metric__value--positive">14.2%</div>
                  </div>
                  <div className="metric">
                    <div className="metric__label">Average Drawdown</div>
                    <div className="metric__value metric__value--negative">-6.3%</div>
                  </div>
                </div>
              </div>
              
              <div className="validation-divider">
                <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                </svg>
              </div>
              
              <div className="validation-panel validation-panel--out-sample">
                <div className="validation-panel__header">
                  <h3 className="validation-panel__title">Out-of-Sample (Holdout)</h3>
                  <div className="validation-panel__period">Nov 2023 - Dec 2023</div>
                </div>
                <div className="validation-panel__metrics">
                  <div className="metric">
                    <div className="metric__label">Realized DSR</div>
                    <div className="metric__value metric__value--warning">0.623</div>
                  </div>
                  <div className="metric">
                    <div className="metric__label">Realized Return</div>
                    <div className="metric__value metric__value--positive">8.7%</div>
                  </div>
                  <div className="metric">
                    <div className="metric__label">Max Drawdown</div>
                    <div className="metric__value metric__value--negative">-9.1%</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Validation Verdict */}
            <div className="validation-verdict">
              <div className="validation-verdict__icon validation-verdict__icon--warning">
                <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="validation-verdict__content">
                <div className="validation-verdict__title">Moderate Performance Degradation Detected</div>
                <div className="validation-verdict__description">
                  Average DSR declined by 26.4% from in-sample to out-of-sample, suggesting some overfitting. 
                  Strategies require additional validation before production deployment.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Analysis Grid */}
      <div className="grid grid--validation">
        {/* DSR Deflation Analysis */}
        <div className="card">
          <div className="card__header">
            <h3 className="card__title">DSR Deflation Waterfall</h3>
            <p className="card__subtitle">Performance degradation breakdown</p>
          </div>
          <div className="card__content">
            <ChartContainer
              type="bar"
              height={250}
              data={{
                labels: ['Raw Sharpe', 'Correlation Adj', 'Trial Adj', 'Length Adj', 'Final DSR'],
                datasets: [{
                  data: [1.23, 0.98, 0.87, 0.79, 0.63],
                  backgroundColor: ['#3b82f6', '#8b5cf6', '#ec4899', '#ef4444', '#22c55e']
                }]
              }}
            />
          </div>
        </div>

        {/* Survivor Performance Distribution */}
        <div className="card">
          <div className="card__header">
            <h3 className="card__title">Holdout Performance</h3>
            <p className="card__subtitle">Out-of-sample DSR distribution</p>
          </div>
          <div className="card__content">
            <ChartContainer
              type="histogram"
              height={250}
              data={{
                datasets: [{
                  data: survivors.map(s => (s.deflated_sharpe_ratio || 0) * 0.74), // Simulate holdout degradation
                  backgroundColor: '#10b981'
                }]
              }}
            />
          </div>
        </div>

        {/* Strategy Comparison Table */}
        <div className="card card--span-2">
          <div className="card__header">
            <h3 className="card__title">Strategy Validation Results</h3>
            <div className="card__actions">
              <select 
                className="select select--sm"
                value={selectedStrategy || ''}
                onChange={(e) => setSelectedStrategy(e.target.value || null)}
              >
                <option value="">All Strategies</option>
                {survivors.map(s => (
                  <option key={s.id} value={s.id}>{s.strategy_name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="card__content">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Strategy</th>
                    <th>In-Sample DSR</th>
                    <th>Holdout DSR</th>
                    <th>Degradation</th>
                    <th>Confidence</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {survivors.slice(0, 10).map((strategy) => {
                    const holdoutDsr = (strategy.deflated_sharpe_ratio || 0) * (0.6 + Math.random() * 0.3)
                    const degradation = ((strategy.deflated_sharpe_ratio || 0) - holdoutDsr) / (strategy.deflated_sharpe_ratio || 1) * 100
                    const confidence = Math.max(0, 100 - degradation * 2)
                    
                    return (
                      <tr key={strategy.id}>
                        <td>
                          <div className="strategy-cell">
                            <div className="strategy-cell__name">{strategy.strategy_name}</div>
                            <div className="strategy-cell__id">{strategy.strategy_id}</div>
                          </div>
                        </td>
                        <td>
                          <span className="metric-value metric-value--positive">
                            {(strategy.deflated_sharpe_ratio || 0).toFixed(3)}
                          </span>
                        </td>
                        <td>
                          <span className={`metric-value ${holdoutDsr > 0.5 ? 'metric-value--positive' : 'metric-value--warning'}`}>
                            {holdoutDsr.toFixed(3)}
                          </span>
                        </td>
                        <td>
                          <span className={`metric-value ${degradation < 20 ? 'metric-value--positive' : degradation < 40 ? 'metric-value--warning' : 'metric-value--negative'}`}>
                            -{degradation.toFixed(1)}%
                          </span>
                        </td>
                        <td>
                          <div className="confidence-bar">
                            <div 
                              className="confidence-bar__fill"
                              style={{ width: `${confidence}%` }}
                            />
                            <span className="confidence-bar__text">{confidence.toFixed(0)}%</span>
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge status-badge--${
                            confidence > 70 ? 'success' : confidence > 40 ? 'warning' : 'error'
                          }`}>
                            {confidence > 70 ? 'Validated' : confidence > 40 ? 'Caution' : 'Failed'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn btn--ghost btn--xs" title="View Details">
                              <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button className="btn btn--ghost btn--xs" title="Re-validate">
                              <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Insights */}
      <div className="card card--warning mt-6">
        <div className="card__header">
          <h3 className="card__title">Validation Insights</h3>
        </div>
        <div className="card__content">
          <div className="insights-grid">
            <div className="insight">
              <div className="insight__icon insight__icon--warning">
                <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                </svg>
              </div>
              <div className="insight__content">
                <h4 className="insight__title">Performance Degradation</h4>
                <p className="insight__description">
                  26.4% average DSR decline from in-sample to holdout suggests moderate overfitting. 
                  Consider longer training periods or additional regularization.
                </p>
              </div>
            </div>
            
            <div className="insight">
              <div className="insight__icon insight__icon--info">
                <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01" />
                </svg>
              </div>
              <div className="insight__content">
                <h4 className="insight__title">Sample Size Considerations</h4>
                <p className="insight__description">
                  2-month holdout period provides limited statistical power. 
                  Consider extending validation window for more robust results.
                </p>
              </div>
            </div>
            
            <div className="insight">
              <div className="insight__icon insight__icon--success">
                <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <div className="insight__content">
                <h4 className="insight__title">Top Performers</h4>
                <p className="insight__description">
                  {survivors.filter((s, i) => i < 3).map(s => s.strategy_name).join(', ')} maintain 
                  strong risk-adjusted returns in holdout testing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}